/**
 * S2 (AB#2205) — display helpers for the staff surfaces (My Day, My Schedule,
 * identity). Pure formatting of what the API returns: no business rules.
 */
import { formatAppointmentDateTime } from './appointmentTime';
import type { StaffAbsenceType, StaffMeDto } from '../types/staffSelf';

type TFn = (key: string) => string;

/** Fill `{name}` placeholders in a translated template. */
export function fill(template: string, values: Record<string, string | number>): string {
    return Object.entries(values).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), template);
}

/** Wrap a value in Unicode isolates so times / Latin names keep their order in Arabic. */
export function isolate(value: string): string {
    return `⁨${value}⁩`;
}

/** Arabic name when the UI is Arabic and one exists, else the default name. */
export function localizedName(name: string | null | undefined, nameAr: string | null | undefined, language: string): string {
    return (language === 'ar' && nameAr) ? nameAr : (name ?? '');
}

/**
 * A salon-local calendar date "YYYY-MM-DD", day-first ("Wed, 4 Nov" / Arabic
 * equivalent). Pinned to UTC so the date never shifts with the viewer's zone.
 */
export function formatDayDate(
    ymd: string | null | undefined,
    language: string,
    opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' },
): string {
    if (!ymd) return '';
    return formatAppointmentDateTime(`${ymd.slice(0, 10)}T00:00:00Z`, language, opts);
}

/**
 * Weekday name for 0 = Sunday … 6 = Saturday. Uses 1–7 Nov 2026 as the reference
 * week: 1 Nov 2026 is a Sunday (pinned by staffDisplay.test.ts), so day N is
 * `2026-11-0(N+1)`, formatted in UTC.
 */
export function weekdayName(dayOfWeek: number, language: string): string {
    const day = String(1 + (((dayOfWeek % 7) + 7) % 7)).padStart(2, '0');
    return formatAppointmentDateTime(`2026-11-${day}T00:00:00Z`, language, { weekday: 'long' });
}

/** "HH:mm – HH:mm", isolated so the range reads in order in Arabic too. */
export function timeRange(start: string | null | undefined, end: string | null | undefined): string {
    if (start && end) return isolate(`${start} – ${end}`);
    return isolate(start ?? end ?? '');
}

const ABSENCE_TYPE_KEY_BY_NAME: Record<string, string> = {
    Break: 'staff.absence.type.break',
    SickLeave: 'staff.absence.type.sick',
    Sick: 'staff.absence.type.sick',
    Vacation: 'staff.absence.type.vacation',
    PersonalTime: 'staff.absence.type.personal',
    Personal: 'staff.absence.type.personal',
    Emergency: 'staff.absence.type.emergency',
};
const ABSENCE_TYPE_KEY_BY_NUMBER = [
    'staff.absence.type.break',
    'staff.absence.type.sick',
    'staff.absence.type.vacation',
    'staff.absence.type.personal',
    'staff.absence.type.emergency',
];

/** The owner-side absence type words (same keys as Manage Staff → Absences). */
export function absenceTypeLabel(type: StaffAbsenceType | null | undefined, t: TFn, labelKey?: string | null): string {
    if (labelKey) {
        const viaKey = t(labelKey);
        if (viaKey !== labelKey) return viaKey;
    }
    if (typeof type === 'number') {
        const key = ABSENCE_TYPE_KEY_BY_NUMBER[type];
        if (key) return t(key);
    } else if (typeof type === 'string') {
        const trimmed = type.trim();
        if (/^\d+$/.test(trimmed) && ABSENCE_TYPE_KEY_BY_NUMBER[Number(trimmed)]) {
            return t(ABSENCE_TYPE_KEY_BY_NUMBER[Number(trimmed)]);
        }
        const key = ABSENCE_TYPE_KEY_BY_NAME[trimmed];
        if (key) return t(key);
    }
    return t('stf.absence.other');
}

/** The role label from /me, in the UI language ("Staff" → Arabic "Staff"). */
export function roleLabelText(label: string | null | undefined, t: TFn): string {
    if (!label || label === 'Staff' || label === 'SalonStaff' || label === 'StaffManager') return t('stf.role.staff');
    return label;
}

/** "Downtown, Marina" — or "All branches" when the staff member isn't restricted. */
export function branchesText(me: StaffMeDto | undefined, language: string, t: TFn): string {
    if (!me) return '';
    if (me.allBranches) return t('stf.identity.allBranches');
    const names = (me.branches ?? [])
        .map(b => localizedName(b.name, b.nameAr, language))
        .filter(Boolean);
    return names.join(language === 'ar' ? '، ' : ', ');
}

/** "Staff · Glamour Palace" — the identity line in the menu and on My Day. */
export function staffIdentityLine(me: StaffMeDto | undefined, language: string, t: TFn): string {
    if (!me) return '';
    const role = roleLabelText(me.roleLabel, t);
    const salon = localizedName(me.businessName, me.businessNameAr, language);
    return salon ? fill(t('stf.identity.roleAtSalon'), { role, salon: isolate(salon) }) : role;
}
